import { humanizeOpportunityTitle } from "./opportunityDisplay";

function nonEmpty(s) {
  if (s == null) return "";
  return String(s).trim();
}

function blobFromOpportunity(opp) {
  return [
    opp?.name,
    opp?.short_description,
    opp?.problem_solved,
    opp?.target_audience,
    opp?.niche,
    opp?.business_model,
    opp?.monetization_strategy,
    opp?.insight,
    opp?.why_trending,
  ]
    .map(nonEmpty)
    .join(" ");
}

function shortDisplayName(raw) {
  let n = humanizeOpportunityTitle(raw || "Product").trim();
  const lower = n.toLowerCase();
  const forIdx = lower.indexOf(" for ");
  if (forIdx > 4) n = n.slice(0, forIdx).trim();
  if (n.length > 52) {
    const cut = n.slice(0, 52).lastIndexOf(" ");
    n = (cut > 16 ? n.slice(0, cut) : n.slice(0, 52)).trim();
  }
  return n || "Web app";
}

/** @returns {'low'|'medium'|'high'} */
function complexityFromOpportunity(opp) {
  const d = Number(opp?.difficulty);
  if (!Number.isFinite(d)) return "medium";
  if (d <= 4) return "low";
  if (d <= 7) return "medium";
  return "high";
}

function hasHeavyNonWebSignals(lower) {
  return /\b(kubernetes|k8s|terraform|ansible|pulumi|nomad|kafka cluster|gpu farm|hardware device|fpga|embedded firmware|native ios app|native android app|desktop-only|electron desktop|cli-only tool|terminal-only)\b/i.test(
    lower
  );
}

/**
 * Classify a single coherent builder intent. Order matters (avoid scanner false positives on security agents).
 * @internal exported for tests
 */
export function classifyBuilderIntent(blobLower, nameLower) {
  const b = blobLower;
  const n = nameLower;

  if (
    /\bmeeting\s+notes\b/i.test(b) ||
    /\b(sales call|call recording|call transcript|zoom recording|voice memo|notetaker)\b/i.test(b) ||
    (/\bmeeting\b/i.test(b) && /\b(crm|summary|action items?|deal|pipeline)\b/i.test(b))
  ) {
    return "meeting_notes";
  }

  const commandTheme =
    /\b(command|shell|powershell|bash|cli prompt|terminal command)\b/.test(b) ||
    /\bcommand execution\b/.test(b) ||
    (/\bhuman[- ]?approved\b/.test(b) && /\bcommand\b/.test(b));
  const approvalTheme =
    /\b(approve|approval|review|pending|gate|policy|human in the loop|before execution|execution security)\b/.test(b) ||
    /\bagent\b/.test(b);
  const websiteScannerTheme =
    (/\b(website|web site|site url|url\b|domain\b|homepage|landing page)\b/.test(b) ||
      /\bwebsite\b/.test(n) ||
      /\bweb\s+app\s+url\b/.test(b)) &&
    /\b(audit|auditor|scanner|scan\b|vulnerab|ssl\b|https\b|security check|csp|hsts|headers\b|pen test|penetration)\b/.test(b);

  if (commandTheme && approvalTheme && !websiteScannerTheme) {
    return "command_approval";
  }

  if (websiteScannerTheme) {
    return "website_scanner";
  }

  if (/\b(inbox|crm|whatsapp|telegram|slack|support ticket|helpdesk|shared inbox)\b/.test(b)) {
    return "inbox_crm";
  }

  if (/\b(form builder|survey|poll|questionnaire|intake form)\b/.test(b)) {
    return "form_intake";
  }

  if (/\b(dashboard|analytics|metrics|kpi|reporting)\b/.test(b) && !websiteScannerTheme) {
    return "simple_dashboard";
  }

  return "generic_tool";
}

function intentPayload(intent, opp, blob) {
  const pitch = nonEmpty(opp.short_description);
  const problem = nonEmpty(opp.problem_solved);
  const audience = nonEmpty(opp.target_audience) || "Small teams who feel this pain day to day.";
  const nameShort = shortDisplayName(opp.name);

  switch (intent) {
    case "command_approval":
      return {
        cleanName: "Command approval tool",
        cleanOneLiner: problem || "Approve or reject sensitive commands before they run.",
        coreUserAction: "Paste or type a command (and optional note) and submit it for approval.",
        coreSystemAction:
          "Store the request as pending, show it in a simple queue, and let an approver mark it approved, rejected, or executed with a timestamp.",
        coreResult: "The submitter sees a clear status card (pending / approved / rejected / executed) and who decided.",
        targetUser: audience,
        excludedFeatures: [
          "Kubernetes or heavy infra",
          "Running arbitrary remote shells from the browser",
          "Mass scanning unrelated third-party websites",
          "Complex RBAC beyond one approver role for MVP",
        ],
        recommendedMonetization:
          "Free: limited pending commands per month. Paid: higher limits + export ($19–39/mo for small teams).",
      };
    case "website_scanner":
      return {
        cleanName: nameShort || "Website security check",
        cleanOneLiner: pitch || "Let users enter a website URL and get a quick security-oriented audit.",
        coreUserAction: "Enter a website URL they control or want to check.",
        coreSystemAction:
          "Fetch public signals server-side (TLS, redirects, basic headers like CSP/HSTS where applicable) and classify obvious issues — no fake random results.",
        coreResult: "A readable list of findings with severity (low / medium / high) and one-line explanations.",
        targetUser: audience,
        excludedFeatures: [
          "Authenticated crawling of private apps behind login (out of scope for MVP)",
          "Full penetration testing automation",
          "Kubernetes or workers beyond a single simple API route",
        ],
        recommendedMonetization: "Free: one scan. Paid: unlimited scans ($19/mo) — adjust if needed.",
      };
    case "meeting_notes":
      return {
        cleanName: nameShort,
        cleanOneLiner: pitch || "Turn a sales call into structured notes and next steps.",
        coreUserAction: "Paste transcript text or upload a small text/markdown file of the call.",
        coreSystemAction:
          "Parse locally or via one API route into summary bullets, action items, and optional CRM-style fields (keep the schema tiny).",
        coreResult: "Show sections: Summary, Action items, and optional fields the user can copy out.",
        targetUser: audience,
        excludedFeatures: [
          "Live dialer or phone system integrations",
          "Unrelated external website security audits",
          "Unrelated shell-command gating queues",
          "Heavy admin panels",
        ],
        recommendedMonetization: "Free: a few calls per month. Paid: unlimited processing ($19–29/mo).",
      };
    case "inbox_crm":
      return {
        cleanName: nameShort,
        cleanOneLiner: pitch || "One inbox to triage short conversations and mark follow-ups.",
        coreUserAction: "Compose or paste a thread snippet and save it as an item.",
        coreSystemAction: "Store items with status (new / waiting / done) and simple labels.",
        coreResult: "A single list with statuses and timestamps — no multi-channel sync in MVP.",
        targetUser: audience,
        excludedFeatures: ["Full email provider OAuth for MVP unless trivial", "Website scanning", "Command approval"],
        recommendedMonetization: "Free: limited items. Paid: unlimited + export ($25/mo).",
      };
    case "form_intake":
      return {
        cleanName: nameShort,
        cleanOneLiner: pitch || "Collect structured answers in one page.",
        coreUserAction: "Fill a short fixed set of fields and submit once.",
        coreSystemAction: "Validate input, store submission, show confirmation.",
        coreResult: "Confirmation + list of recent submissions for the creator.",
        targetUser: audience,
        excludedFeatures: ["Website scanning", "Command approval", "Complex branching logic builders"],
        recommendedMonetization: "Free: low submission cap. Paid: higher cap ($15/mo).",
      };
    case "simple_dashboard":
      return {
        cleanName: nameShort,
        cleanOneLiner: pitch || "See a few key numbers from pasted or demo data.",
        coreUserAction: "Paste a tiny CSV or pick a demo dataset.",
        coreSystemAction: "Parse client-side or one API route and render 3–5 cards/charts max.",
        coreResult: "A compact dashboard with clear labels and one refresh action.",
        targetUser: audience,
        excludedFeatures: ["Enterprise BI", "Website scanning", "Command approval", "Many chart types"],
        recommendedMonetization: "Free: demo data only. Paid: saved datasets ($19/mo).",
      };
    default:
      return {
        cleanName: nameShort,
        cleanOneLiner: pitch || problem || `A focused web tool for: ${blob.slice(0, 120)}…`,
        coreUserAction: "Provide the one main input this product needs (text, link, or file — pick the smallest honest default).",
        coreSystemAction: "Run the core transformation or lookup and persist the minimal record if needed.",
        coreResult: "Show a clear outcome card plus a sensible next step (retry, copy, or upgrade).",
        targetUser: audience,
        excludedFeatures: [
          "Unrelated website security scanning unless this product is explicitly a scanner",
          "Command approval queues unless this product is explicitly about commands",
          "Kubernetes, heavy microservices, native mobile apps",
        ],
        recommendedMonetization: "Free: limited usage. Paid: unlimited ($19/mo) after the first success.",
      };
  }
}

/**
 * Normalize a raw SaaSRadar opportunity into a single coherent “builder” idea.
 *
 * @param {Record<string, any>} opportunity
 * @param {{ intel?: Record<string, any> | null }} [ctx]
 * @returns {{
 *   opportunityId: string,
 *   cleanName: string,
 *   cleanOneLiner: string,
 *   coreUserAction: string,
 *   coreSystemAction: string,
 *   coreResult: string,
 *   targetUser: string,
 *   buildComplexity: 'low'|'medium'|'high',
 *   isWebAppBuildable: boolean,
 *   excludedFeatures: string[],
 *   recommendedMonetization: string,
 *   builderIntent: string,
 *   marketHintLine: string,
 * }}
 */
export function normalizeOpportunityForBuilderPrompt(opportunity, ctx = {}) {
  const opp = opportunity || {};
  const oid = nonEmpty(opp.opportunity_id || opp.id) || "unknown";
  const blob = blobFromOpportunity(opp);
  const blobLower = blob.toLowerCase();
  const nameLower = nonEmpty(opp.name).toLowerCase();

  const intel = ctx?.intel || null;
  const bench = intel?.benchmarks || {};
  const marketHintLine =
    bench?.sample_size > 0 && bench.avg_mrr_usd != null
      ? `SaaSRadar market hint: n≈${bench.sample_size}, avg peer MRR ~$${Number(bench.avg_mrr_usd).toLocaleString()} (directional).`
      : "SaaSRadar: directional context only — prioritize the normalized flow below.";

  const heavy = hasHeavyNonWebSignals(blobLower);
  const intent = classifyBuilderIntent(blobLower, nameLower);
  const payload = intentPayload(intent, opp, blob);

  let buildComplexity = complexityFromOpportunity(opp);
  if (heavy) buildComplexity = "high";

  return {
    opportunityId: oid,
    cleanName: payload.cleanName,
    cleanOneLiner: nonEmpty(payload.cleanOneLiner),
    coreUserAction: nonEmpty(payload.coreUserAction),
    coreSystemAction: nonEmpty(payload.coreSystemAction),
    coreResult: nonEmpty(payload.coreResult),
    targetUser: nonEmpty(payload.targetUser),
    buildComplexity,
    isWebAppBuildable: !heavy,
    excludedFeatures: payload.excludedFeatures,
    recommendedMonetization: nonEmpty(payload.recommendedMonetization),
    builderIntent: intent,
    marketHintLine,
  };
}
